import classNames from "classnames";
import React, { useLayoutEffect, useRef } from "react";
import { IPaneSwap, usePaneSwap } from "../../../shared/hooks/usePaneSwap";
import usePrefersReducedMotion from "../../../shared/hooks/usePrefersReducedMotion";

export type KModalStepKey = string | number;

export interface IKModalStepBodyProps {
  stepKey: KModalStepKey;
  children: React.ReactNode;
}

const MORPHING_CLASS: string = "k-modal-step-body--morphing";

/**
 * The body of a multi-step KModal (`stepKey`). When the key changes, the old step leaves
 * (`kuika-pane-out`, aria-hidden, no pointer) while the new one arrives (`kuika-pane-in`) in the same
 * grid cell, and the frame animates its height from the old step's to the new one's
 * (`--dur-layout`, the layout spring), then hands the height back to `auto`.
 *
 * Which step is shown and which is leaving is `usePaneSwap`, shared with `KuikaTabContent`; the
 * height morph is this component's own. A tab pane can be re-rendered from its key, but a step
 * cannot — only the current step is passed in — so the swap remembers the node the last commit
 * rendered and shows that while it leaves.
 *
 * - The first render never animates; only step changes do.
 * - Re-rendering with the same key updates the current step in place.
 * - A change during a morph starts from the height the frame has reached.
 * - Reduced motion: no leaver and no height morph — the new step replaces the old at once and only
 *   fades in (the pane keyframes lose their travel through --motion-travel).
 */
const KModalStepBody = ({ stepKey, children }: IKModalStepBodyProps): JSX.Element => {
  const currentKey: string = String(stepKey);
  const prefersReducedMotion: boolean = usePrefersReducedMotion();
  const frameRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const incomingRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const leavingRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  // What the last commit drew. Written after every commit, so the render that starts a swap reads
  // the step that is on screen at that moment.
  const committedNodeRef: React.MutableRefObject<React.ReactNode> = useRef<React.ReactNode>(children);

  const swap: IPaneSwap<React.ReactNode> = usePaneSwap<React.ReactNode>(
    currentKey,
    !prefersReducedMotion,
    (): React.ReactNode => committedNodeRef.current
  );

  useLayoutEffect((): void => {
    committedNodeRef.current = children;
  }, [currentKey, children]);

  // Runs once the leaver and the new step are both laid out, still before paint.
  useLayoutEffect((): void => {
    const frame: HTMLDivElement | null = frameRef.current;
    const incoming: HTMLDivElement | null = incomingRef.current;
    const leaving: HTMLDivElement | null = leavingRef.current;

    if (frame === null || incoming === null || leaving === null) {
      return;
    }

    const isMorphing: boolean = frame.classList.contains(MORPHING_CLASS);
    const fromHeight: number = isMorphing ? frame.getBoundingClientRect().height : leaving.offsetHeight;
    const toHeight: number = incoming.offsetHeight;

    if (fromHeight === toHeight) {
      return;
    }

    frame.classList.remove(MORPHING_CLASS);
    frame.style.height = `${fromHeight}px`;
    // Commit the start height before the transition is switched on, or the browser skips to the end.
    frame.getBoundingClientRect();
    frame.classList.add(MORPHING_CLASS);
    frame.style.height = `${toHeight}px`;
  }, [swap.leaverKey]);

  const settleHeight = (event: React.TransitionEvent<HTMLDivElement>): void => {
    // Transitions inside the steps bubble here too; only the frame's own height ends the morph.
    if (event.target !== event.currentTarget || event.propertyName !== "height") {
      return;
    }

    event.currentTarget.classList.remove(MORPHING_CLASS);
    event.currentTarget.style.height = "";
  };

  return (
    <div ref={frameRef} className="k-modal-step-body" onTransitionEnd={settleHeight}>
      {swap.leaverKey !== null && (
        <div
          key={swap.leaverKey}
          ref={leavingRef}
          className="k-modal-step-body__step k-modal-step-body__step--out"
          aria-hidden="true"
          onAnimationEnd={swap.onLeaverAnimationEnd}
        >
          {swap.detail}
        </div>
      )}
      <div
        key={currentKey}
        ref={incomingRef}
        className={classNames("k-modal-step-body__step", { "k-modal-step-body__step--in": swap.hasSwapped })}
      >
        {children}
      </div>
    </div>
  );
};

export default KModalStepBody;
